'use client';

import Image from '@/components/Image';
import Wish from '@/components/Product/Wish';
import { useAppNavigation } from '@/hooks/useAppNavigation';
import { useStore } from '@/providers/app-store-provider';
import { formatPriceNumberWithSymbol, priceInfo as getPriceInfo } from '@/utils/price';
import React, { useEffect, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { Swiper as SwiperType } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import { PgProduct, PgProductSubComponentProps } from './PgProductTypes';

// 曝光检测组件
const ExposureDetector: React.FC<{
  product: PgProduct;
  productIndex: number;
  onExposure: (product: PgProduct) => void;
}> = React.memo(({ product, onExposure }) => {
  const [ref, inView] = useInView({
    threshold: 0.8,
    triggerOnce: true,
  });

  useEffect(() => {
    if (inView && product.id) {
      onExposure(product);
    }
  }, [inView]);

  return <div ref={ref} className="absolute inset-0 pointer-events-none" />;
});

ExposureDetector.displayName = 'ExposureDetector';

/**
 * WAP端商品组展示组件
 */
const WapProductGroupItem: React.FC<PgProductSubComponentProps> = React.memo(
  ({ productGroup, onProductClick, onProductExposure }) => {
    const { navigate } = useAppNavigation();
    const currency = useStore((state) => state.currency);
    const userInfo = useStore((state) => state.userInfo);

    // 当前显示的商品索引
    const [currentProductIndex, setCurrentProductIndex] = useState(0);
    // Swiper引用
    const materialSwiperRef = useRef<SwiperType | null>(null);
    const cardsSwiperRef = useRef<SwiperType | null>(null);

    // 获取当前商品
    const currentProduct = productGroup[currentProductIndex] || productGroup[0];

    // 监听 currentProductIndex 变化，同步移动端的两个轮播
    useEffect(() => {
      const targetIndex = currentProductIndex;

      const syncSwipers = () => {
        const materialSwiper = materialSwiperRef.current;
        const cardsSwiper = cardsSwiperRef.current;

        // 同步背景轮播
        if (materialSwiper && !materialSwiper.destroyed) {
          if (materialSwiper.activeIndex !== targetIndex) {
            materialSwiper.slideTo(targetIndex, 300);
          }
        }

        // 同步卡片轮播
        if (cardsSwiper && !cardsSwiper.destroyed) {
          if (cardsSwiper.activeIndex !== targetIndex) {
            cardsSwiper.slideTo(targetIndex, 300);
          }
        }
      };

      const rafId = requestAnimationFrame(syncSwipers);
      return () => cancelAnimationFrame(rafId);
    }, [currentProductIndex]);

    // 缩略图点击切换
    const handleProductSwitch = (index: number) => {
      if (index === currentProductIndex) return;
      setCurrentProductIndex(index);
    };

    // 商品跳转
    const toProductDetail = (product: PgProduct) => {
      navigate(`/goods-detail/${product.shop_sku}`);
    };

    // 获取商品价格信息
    const getProductPriceInfo = (item: PgProduct) => {
      try {
        return getPriceInfo(userInfo, item, currency);
      } catch {
        return { price: '', sell_price: '' };
      }
    };

    // 处理商品收藏状态变化
    const handleProductWished = (productId: number) => {
      const product = productGroup.find((p) => p.id === productId);
      if (product) {
        product.is_wish = 1;
      }
    };

    const handleProductRemoved = (productId: number) => {
      const product = productGroup.find((p) => p.id === productId);
      if (product) {
        product.is_wish = 2;
      }
    };

    // 将产品数据适配为Wish组件期望的格式
    const adaptProductForWish = (product: PgProduct) => {
      return {
        ...product,
        image: {
          product: [product.image || ''],
          material: [],
          celebrity: [],
          video: [],
        },
      } as any;
    };

    if (!productGroup || productGroup.length === 0) {
      return null;
    }

    return (
      <div className="w-full px-[14px]">
        {/* 顶部商品信息区域 */}
        <div className="flex items-center gap-[20px] mb-[13px] h-[64px]">
          {/* 商品缩略图列表 */}
          <div className="flex items-center gap-[20px]">
            {productGroup.map((product, index) => (
              <div
                key={index}
                className={`border border-transparent p-[2px] box-border rounded-full ${
                  currentProductIndex === index ? 'border-white' : ''
                }`}
                onClick={() => {
                  handleProductSwitch(index);
                }}
              >
                <div className="w-[45px] h-[45px] rounded-full bg-[#eeeeee] flex items-center justify-center overflow-hidden p-[5px] box-border">
                  <Image
                    src={product.image}
                    alt={product.short_name}
                    width={45}
                    height={45}
                    className="w-full h-auto object-cover mix-blend-multiply"
                  />
                </div>
              </div>
            ))}
          </div>

          {/* 商品信息 */}
          <div className="text-white">
            <h3 className="text-[24px] font-bb text-white">{currentProduct.short_name}</h3>
            <p className="text-[12px] leading-[14px] font-br text-white">{currentProduct.name}</p>
          </div>
        </div>

        {/* 背景素材图轮播区域 */}
        <div className="relative h-[463px] rounded-[12px] overflow-hidden">
          {/* 背景素材图轮播 */}
          <Swiper
            allowTouchMove
            autoplay={false}
            loop={false}
            speed={300}
            onInit={(swiper) => {
              materialSwiperRef.current = swiper;
            }}
            onSlideChange={(swiper) => {
              const newIndex = swiper.activeIndex;
              if (newIndex !== currentProductIndex) {
                setCurrentProductIndex(newIndex);
              }
            }}
            className="w-full h-full"
          >
            {productGroup.map((product, index) => (
              <SwiperSlide key={index} className="w-full h-full relative">
                <div
                  className="w-full h-full relative cursor-pointer"
                  onClick={() => {
                    onProductClick(product);
                    toProductDetail(product);
                  }}
                >
                  <Image
                    src={product.config_image}
                    alt={product.short_name}
                    width={400}
                    height={463}
                    className="w-full h-full object-cover object-center"
                  />
                </div>
                {/* 曝光检测组件 */}
                <ExposureDetector
                  product={product}
                  productIndex={index}
                  onExposure={onProductExposure}
                />
              </SwiperSlide>
            ))}
          </Swiper>

          {/* 固定在背景上的商品卡片轮播 */}
          <div className="absolute bottom-0 left-0 right-0 z-10 p-[0_0_12px]">
            <Swiper
              onInit={(swiper) => {
                cardsSwiperRef.current = swiper;
              }}
              onSlideChange={(swiper) => {
                const newIndex = swiper.activeIndex;
                if (newIndex !== currentProductIndex) {
                  setCurrentProductIndex(newIndex);
                }
              }}
              slidesPerView="auto"
              spaceBetween={12}
              slidesPerGroup={1}
              slidesOffsetBefore={12}
              slidesOffsetAfter={12}
              loop={false}
              allowTouchMove
              speed={300}
              className="w-full"
              centeredSlides
              centeredSlidesBounds
            >
              {productGroup.map((product, index) => {
                // 为每个商品获取对应的价格信息
                const productPriceData = getProductPriceInfo(product);

                return (
                  <SwiperSlide key={index} className="!w-auto">
                    <div
                      className={`flex items-center w-[264px] p-[10px] box-border bg-white rounded-[10px] overflow-hidden cursor-pointer`}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onProductClick(product);
                        toProductDetail(product);
                      }}
                    >
                      {/* 商品图片 */}
                      <div className="flex items-center justify-center bg-[#eeeeee] w-[64px] h-[64px] rounded-[6px]">
                        <Image
                          src={product.image}
                          alt={product.short_name}
                          width={64}
                          height={64}
                          className="w-[80%] h-auto mix-blend-multiply"
                        />
                      </div>

                      {/* 商品信息 */}
                      <div className="flex-1 ml-[10px]">
                        <div className="flex justify-between items-center">
                          <p className="text-[12px] text-black">{product.short_name}</p>
                          {/* 收藏按钮 */}
                          <div className="w-[24px] h-[24px] z-20">
                            <Wish
                              data={adaptProductForWish(product)}
                              className=""
                              iconClassName="!text-[20px] text-[#282828] hover:text-primary-purple"
                              collectScene="坑位收藏"
                              wished={() => handleProductWished(product.id)}
                              removed={() => handleProductRemoved(product.id)}
                            />
                          </div>
                        </div>
                        <div className="text-black">
                          <span className="text-[14px] font-bb">
                            {formatPriceNumberWithSymbol(productPriceData.sell_price, currency)}
                          </span>
                          {productPriceData.sell_price !== productPriceData.price && (
                            <span className="text-[12px] text-[#a9a9a9] line-through ml-[8px]">
                              {formatPriceNumberWithSymbol(productPriceData.price, currency)}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </SwiperSlide>
                );
              })}
            </Swiper>
          </div>
        </div>
      </div>
    );
  },
);

WapProductGroupItem.displayName = 'WapProductGroupItem';

export default WapProductGroupItem;
